import { supabase, isConfigured, requireClient } from './supabase.js';
import { currentUser, isAnonymous } from './auth.js';
import { emit, on } from '../state/bus.js';

/* A message to the owner, about credits or a licence — the two things still
   arranged directly rather than self-serve. Table and function names here
   still say "trial" (trial_requests, my_trial_request, admin_list_trials):
   that is a rename of its own, with a migration and a PRIVACY.md update to
   go with it, and not what changed when the trial concept itself went away.
   What matters is that nothing built on this file still talks about one.

   A single insert into a table the asker can read only their own row of, the
   same shape as feedback (0006) — except that this one is answered by a
   person rather than filed, so supabase/functions/trial-notify sends it on
   immediately instead of waiting for a daily digest.

   THE ADDRESS IS NOT A FIELD. Everything else on the form is typed; the email
   comes from the session and cannot be edited, because motion_licences is
   keyed on email and a request naming an address the person does not sign in
   with produces a licence that is granted, invisible, and reported back as
   "it still says locked". The insert policy in 0016 refuses a mismatch, so
   this is enforced twice: the form does not offer it, and the database would
   not accept it. */

/* Cached so the locked surfaces can render synchronously — they repaint on
   motion:changed and auth:changed and must not each fire their own query.
   null means "not asked" OR "not known yet"; the distinction never matters to
   a caller, because both render the same button. */
let state = null;

export const trialStatus = () => state?.status || null;
export const trialAsked = () => state?.status === 'new';

/** The four the form offers, and the four the column's check constraint
 *  allows. Defined here so the <select> cannot drift from the constraint —
 *  a mismatch would be a failed insert at submit time rather than at build
 *  time, which is the worst place to find out. */
export const BUSINESS_TYPES = [
  'Agency', 'Media owner', 'Brand or advertiser', 'Production company', 'Other'
];

/** Reads the caller's own most recent request. Fails closed and quiet: not
 *  knowing must look like not having asked, so the button still works. */
export async function loadTrialStatus() {
  if (!isConfigured || !currentUser() || isAnonymous()) { state = null; return null; }
  const { data, error } = await supabase.rpc('my_trial_request');
  if (error) {
    console.warn('[illusio] trial status failed', error.message);
    return state;                        // keep what we had; a blip is not a no
  }
  const row = Array.isArray(data) ? data[0] : data;
  state = row || null;
  emit('trial:changed');
  return state;
}

on('auth:changed', () => { loadTrialStatus(); });

/**
 * File one. Everything but the address is the caller's to supply.
 *
 * @param {{name:string, company:string, country:string, businessType:string,
 *          message?:string, source?:'motion'|'licence'}} f
 */
export async function sendTrialRequest(f) {
  if (!isConfigured) throw new Error('That needs Supabase credentials — see the README.');

  /* Not ensureSession() like feedback does. An anonymous session would get a
     row with no address to grant against, so this genuinely requires an
     account and says so rather than quietly creating one. */
  const user = currentUser();
  if (!user || isAnonymous() || !user.email)
    throw new Error('Create an account first — this is tied to an email address.');

  const name = String(f.name || '').trim();
  const company = String(f.company || '').trim();
  const country = String(f.country || '').trim();
  const businessType = String(f.businessType || '').trim();
  const message = String(f.message || '').trim();

  /* Checked here so a missing field is refused while it can still be typed,
     rather than after a round trip. Mirrors the column constraints. */
  if (!name) throw new Error('Your name, please.');
  if (!company) throw new Error('Which company are you with?');
  if (!country) throw new Error('Which country?');
  if (!BUSINESS_TYPES.includes(businessType)) throw new Error('Pick what kind of business you are.');
  if (message.length > 2000) throw new Error('That message is a little long — 2000 characters is the limit.');

  const { error } = await supabase.from('trial_requests').insert({
    user_id: user.id,
    email: user.email,
    name, company, country,
    business_type: businessType,
    /* Which of the two things prompted this — worth keeping even though the
       form no longer says anything different depending on it (ui/trial.js's
       DOORS collapsed to one), because it is still one honest signal about
       what to lead with in the reply: credits, or a licence. Anything
       unrecognised falls back rather than failing the insert — a column
       constraint should not be able to lose a lead. */
    source: f.source === 'licence' ? 'licence' : 'motion',
    message: message || null
  });

  if (error) {
    /* The one-open-request-per-address index. Not an error from the asker's
       point of view — it means the thing they wanted has already happened. */
    if (error.code === '23505') {
      await loadTrialStatus();
      throw new Error('You have already asked — we will be in touch shortly.');
    }
    console.error('[illusio] trial request failed', error);
    throw new Error("Couldn't send that just now. Try again in a moment.");
  }

  await loadTrialStatus();
}

/* ── the owner's queue ──────────────────────────────────────────────────────
   Admin-only by RLS, so these are safe to call as anyone: a non-admin gets an
   empty list rather than an error, and the UI never has to ask permission
   before deciding what to draw. */

export async function listTrials(limit = 200) {
  const { data, error } = await requireClient()
    .rpc('admin_list_trials', { p_limit: limit });
  if (error) { console.warn('[illusio] trial list failed', error.message); return []; }
  return data || [];
}

/** Remove a request entirely. Distinct from dismissing it: dismiss is an
 *  answer and keeps the record, this is for the rows that were never a
 *  request — tests, duplicates, spam. 0016's admin policy is `for all`, so
 *  no migration was needed for this one. */
export async function deleteTrial(id) {
  const { error } = await requireClient().from('trial_requests').delete().eq('id', id);
  if (error) {
    console.error('[illusio] trial delete failed', error);
    throw new Error("Couldn’t delete that request.");
  }
}

export async function setTrialStatus(id, status) {
  const { error } = await requireClient()
    .rpc('admin_set_trial_status', { p_id: id, p_status: status });
  if (error) {
    console.error('[illusio] trial status update failed', error);
    throw new Error("Couldn't update that request.");
  }
}
